/**
 * Task List View Queries (End User Data Viewing)
 *
 * React Query hooks for displaying task data in list views.
 * Phase 1: Using mock data
 * Phase 2: Will integrate with Object Manager list view configurations
 *
 * NOTE: This is separate from /services/queries/listViewQueries.ts which is
 * for Object Manager admin configuration.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { ListView, ListViewRecord, ListViewData } from '../../types/listView';
import { persistedTaskListViews, persistedTaskRecords } from '../mockData/tasks';
import { getRecordsForObjectPage } from '../recordStorageService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { USE_RDS_FOR_CORE_OBJECTS, isRdsCoreObject } from '../../config/rdsConfig';
import { normalizeRecordForListView } from '../../utils/listViewRecordNormalizer';

// Mock list views for Tasks
const mockTaskListViews: ListView[] = [
  {
    id: 'lv_tasks_all',
    name: 'All Tasks',
    apiName: 'all_tasks',
    objectApiName: 'Task',
    description: 'All task records',
    isDefault: true,
    columns: [
      { fieldApiName: 'title', fieldLabel: 'Task Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 1 },
      { fieldApiName: 'priority', fieldLabel: 'Priority', fieldType: 'badge', isVisible: true, order: 2 },
      { fieldApiName: 'assignee', fieldLabel: 'Assignee', fieldType: 'text', isVisible: true, order: 3 },
      { fieldApiName: 'dueDate', fieldLabel: 'Due Date', fieldType: 'date', isVisible: true, order: 4 },
      { fieldApiName: 'type', fieldLabel: 'Type', fieldType: 'text', isVisible: true, order: 5 },
    ],
    createdBy: 'System',
    createdDate: '2024-01-01T00:00:00Z'
  },
  {
    id: 'lv_tasks_open',
    name: 'Open Tasks',
    apiName: 'open_tasks',
    objectApiName: 'Task',
    description: 'Tasks with Open or In Progress status',
    isDefault: false,
    columns: [
      { fieldApiName: 'title', fieldLabel: 'Task Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'priority', fieldLabel: 'Priority', fieldType: 'badge', isVisible: true, order: 1 },
      { fieldApiName: 'assignee', fieldLabel: 'Assignee', fieldType: 'text', isVisible: true, order: 2 },
      { fieldApiName: 'dueDate', fieldLabel: 'Due Date', fieldType: 'date', isVisible: true, order: 3 },
      { fieldApiName: 'type', fieldLabel: 'Type', fieldType: 'text', isVisible: true, order: 4 },
    ],
    filters: [
      { fieldApiName: 'status', operator: 'notEquals', value: 'Completed' }
    ],
    createdBy: 'Sarah Johnson',
    createdDate: '2024-02-15T10:30:00Z'
  },
  {
    id: 'lv_tasks_high_priority',
    name: 'High Priority',
    apiName: 'high_priority_tasks',
    objectApiName: 'Task',
    description: 'High priority tasks',
    isDefault: false,
    columns: [
      { fieldApiName: 'title', fieldLabel: 'Task Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 1 },
      { fieldApiName: 'assignee', fieldLabel: 'Assignee', fieldType: 'text', isVisible: true, order: 2 },
      { fieldApiName: 'dueDate', fieldLabel: 'Due Date', fieldType: 'date', isVisible: true, order: 3 },
      { fieldApiName: 'slaTarget', fieldLabel: 'SLA (days)', fieldType: 'number', isVisible: true, order: 4 },
    ],
    filters: [
      { fieldApiName: 'priority', operator: 'equals', value: 'High' }
    ],
    sortBy: {
      fieldApiName: 'dueDate',
      direction: 'asc'
    },
    createdBy: 'Michael Chen',
    createdDate: '2024-03-10T14:00:00Z'
  },
  {
    id: 'lv_tasks_my',
    name: 'My Tasks',
    apiName: 'my_tasks',
    objectApiName: 'Task',
    description: 'Tasks assigned to me',
    isDefault: false,
    columns: [
      { fieldApiName: 'title', fieldLabel: 'Task Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 1 },
      { fieldApiName: 'priority', fieldLabel: 'Priority', fieldType: 'badge', isVisible: true, order: 2 },
      { fieldApiName: 'dueDate', fieldLabel: 'Due Date', fieldType: 'date', isVisible: true, order: 3 },
      { fieldApiName: 'type', fieldLabel: 'Type', fieldType: 'text', isVisible: true, order: 4 },
    ],
    filters: [
      { fieldApiName: 'assignee', operator: 'equals', value: 'Current User' }
    ],
    createdBy: 'John Smith',
    createdDate: '2024-04-01T10:00:00Z'
  }
];

// Mock task records
const mockTaskRecords: ListViewRecord[] = [
  {
    id: '1',
    title: 'Process credit dispute request',
    type: 'Credit Repair',
    status: 'In Progress',
    priority: 'High',
    assignee: 'Sarah Johnson',
    dueDate: '2025-09-20',
    slaTarget: 2,
    source: 'Email-to-Case',
    clientId: '1'
  },
  {
    id: '2',
    title: 'Setup credit monitoring',
    type: 'Credit Management',
    status: 'Open',
    priority: 'Medium',
    assignee: 'Mike Chen',
    dueDate: '2025-09-22',
    slaTarget: 3,
    source: 'Manual',
    clientId: '2'
  },
  {
    id: '3',
    title: 'Unlock credit with Experian',
    type: 'Credit Block',
    status: 'Completed',
    priority: 'Low',
    assignee: 'Lisa Brown',
    dueDate: '2025-09-18',
    slaTarget: 1,
    source: 'Email-to-Case',
    clientId: '1'
  },
  {
    id: '4',
    title: 'Review client documentation',
    type: 'ID Theft Recovery',
    status: 'Open',
    priority: 'High',
    assignee: 'John Smith',
    dueDate: '2025-09-21',
    slaTarget: 2,
    source: 'Manual',
    clientId: '2'
  },
  {
    id: '5',
    title: 'Credit development consultation',
    type: 'Credit Development',
    status: 'In Progress',
    priority: 'Medium',
    assignee: 'John Smith',
    dueDate: '2025-09-25',
    slaTarget: 5,
    source: 'Phone',
    clientId: '3'
  },
  {
    id: '6',
    title: 'Follow up on dispute resolution',
    type: 'Credit Repair',
    status: 'Completed',
    priority: 'Low',
    assignee: 'John Smith',
    dueDate: '2025-09-15',
    slaTarget: 3,
    source: 'Email-to-Case',
    clientId: '1'
  },
  {
    id: '7',
    title: 'Review credit report updates',
    type: 'Credit Monitoring',
    status: 'Open',
    priority: 'High',
    assignee: 'John Smith',
    dueDate: '2025-09-23',
    slaTarget: 1,
    source: 'System',
    clientId: '3'
  }
];

/**
 * Get available list views for tasks
 */
export function useTaskListViews() {
  return useQuery({
    queryKey: ['taskListViews'],
    queryFn: async () => {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));

      return persistedTaskListViews.get();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

const DEFAULT_PAGE_SIZE = 25;

/**
 * Get list view data (view configuration + records) with server-side pagination
 */
export function useTaskListViewData(
  listViewId: string,
  options?: { page?: number; pageSize?: number }
) {
  const page = options?.page ?? 0;
  const pageSize = options?.pageSize ?? DEFAULT_PAGE_SIZE;
  const useBackend = shouldUseGobodo() || (USE_RDS_FOR_CORE_OBJECTS && isRdsCoreObject('Task'));

  return useQuery<ListViewData>({
    queryKey: ['taskListViewData', listViewId, page, pageSize],
    queryFn: async () => {
      const allViews = persistedTaskListViews.get();
      const listView = allViews.find(lv => lv.id === listViewId);
      if (!listView) throw new Error('List view not found');

      if (useBackend) {
        const { records: pageRecords, totalRecords } = await getRecordsForObjectPage('Task', { page, size: pageSize });
        const seen = new Set<string>();
        let records: ListViewRecord[] = pageRecords
          .map(r => normalizeRecordForListView('Task', r) as ListViewRecord)
          .filter(r => {
            if (seen.has(r.id)) return false;
            seen.add(r.id);
            return true;
          });

        if (listView.filters?.length) {
          listView.filters.forEach(filter => {
            records = records.filter(record => {
              const fieldValue = record[filter.fieldApiName];
              switch (filter.operator) {
                case 'equals':
                  if (filter.value === 'Current User') return fieldValue === 'John Smith';
                  return fieldValue === filter.value;
                case 'notEquals': return fieldValue !== filter.value;
                case 'contains': return String(fieldValue).toLowerCase().includes(String(filter.value).toLowerCase());
                case 'startsWith': return String(fieldValue).toLowerCase().startsWith(String(filter.value).toLowerCase());
                default: return true;
              }
            });
          });
        }

        if (listView.sortBy) {
          records = [...records].sort((a, b) => {
            const aValue = a[listView.sortBy!.fieldApiName];
            const bValue = b[listView.sortBy!.fieldApiName];
            if (listView.sortBy!.direction === 'asc') return aValue > bValue ? 1 : -1;
            return aValue < bValue ? 1 : -1;
          });
        }
        return { listView, records, totalRecords };
      }

      const allRecords = persistedTaskRecords.get();
      let filtered: ListViewRecord[] = [...allRecords];
      if (listView.filters?.length) {
        listView.filters.forEach(filter => {
          filtered = filtered.filter(record => {
            const fieldValue = record[filter.fieldApiName];
            switch (filter.operator) {
              case 'equals':
                if (filter.value === 'Current User') return fieldValue === 'John Smith';
                return fieldValue === filter.value;
              case 'notEquals': return fieldValue !== filter.value;
              case 'contains': return String(fieldValue).toLowerCase().includes(String(filter.value).toLowerCase());
              case 'startsWith': return String(fieldValue).toLowerCase().startsWith(String(filter.value).toLowerCase());
              default: return true;
            }
          });
        });
      }
      if (listView.sortBy) {
        filtered = [...filtered].sort((a, b) => {
          const aValue = a[listView.sortBy!.fieldApiName];
          const bValue = b[listView.sortBy!.fieldApiName];
          if (listView.sortBy!.direction === 'asc') return aValue > bValue ? 1 : -1;
          return aValue < bValue ? 1 : -1;
        });
      }
      const totalRecords = filtered.length;
      const start = page * pageSize;
      const records = filtered.slice(start, start + pageSize);
      return { listView, records, totalRecords };
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!listViewId,
    placeholderData: keepPreviousData,
  });
}

/**
 * Get default list view for tasks
 */
export function useDefaultTaskListView() {
  return useQuery({
    queryKey: ['defaultTaskListView'],
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 200));

      const allViews = persistedTaskListViews.get();
      const defaultView = allViews.find(lv => lv.isDefault);

      return defaultView || allViews[0];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Update task list view
 */
export function useUpdateTaskListView() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ListView> }) => {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      // Update persisted data
      const updated = persistedTaskListViews.update(id, {
        ...updates,
        lastModifiedDate: new Date().toISOString()
      });

      if (!updated) {
        throw new Error('List view not found');
      }

      return updated;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['taskListViews'] });
      queryClient.invalidateQueries({ queryKey: ['taskListViewData', data.id] });
    },
  });
}

/**
 * Format date for display
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}
